<!--
//================================================================================
// ID:-
// ProjectName: 都営住宅募集オンライン申請
// SystemName: 都営住宅募集オンラインシステム
// JavaScriptName:ListBox
//
// <<Modification History>>
// Version  | Date       | Updated By            | Content
// ---------+------------+-----------------------+--------------------------------
// 01.00.00 | 2021/08/06 | 富士通株式会社         | 新規作成
// 01.01.00 | 2024/03/29 | 富士通Japan株式会社    | 令和5年度改修対応（3次リリース）
//================================================================================
-->
<template>
	<select
		v-bind="$attrs"
		ref="selectListBox"
		:value="modelValue"
		:class="[{'contents__error--item': errorFlg}, {'contents__input--select-disabled': disabledFlg}]"
		:disabled="disabledFlg"
		:tabindex="tabindex"
		@change="onChangeValue"
		@blur="$emit('blur', $event)"
		@keydown="$emit('keydown', $event)"
	>
		<option
			v-for="option in options"
			:key="option.key"
			:value="option.key"
			:selected="value === option.key"
		>
			{{ option.val }}
		</option>
		<optgroup
			v-if="reducedDisplayFlg && mobileFlg"
			label=""
		/>
	</select>
</template>

<script>
import { co_post } from '@/common/ajaxUtility.js';
import { co_isEmpty } from '@/common/checkUtility.js';
import { co_getMessage } from '@/common/messageUtility.js';

import { CD_HANDLE_TYPE } from '@/const/const.js';
import { API_LIST_ITEMS } from '@/const/route.js';
import { STORE_ERROR_SET_INPUT_ERR, STORE_ERROR_IS_ERRFLG, STORE_ERROR_IS_INPUT_ERRFLG, STORE_VIEW_IS_MOBILE } from '@/const/store.js';

export default {
	inheritAttrs: false,
	props: {
		modelValue: {type: String, default: ''},
		name: {type: String, default: ''},
		requireFlg: {type: Boolean, default: false},			// true : 必須
		listItems: {type: Array, default: function() {
				return []
			}
		},														// リスト作成用のjson
		cdType: {type: String, default: ''},					// リスト作成用のタイプコード
		cdHanyo1: {type: String, default: ''},					// リスト作成用の汎用コード１
		cdHanyo2: {type: String, default: ''},					// リスト作成用の汎用コード２
		cdHanyo3: {type: String, default: ''},					// リスト作成用の汎用コード３
		cdKbn: {type: String, default: CD_HANDLE_TYPE.ID_CD },	// リスト作成用のコード分類
		blankFlg: {type: Boolean, default: true},				// リストの先頭の空白有無 true:空白あり、false:空白なし
		disabledFlg: { type: Boolean, default: false },			// 活性・非活性フラグ
		tabindex: {type: String, default: '0'},					// タブインデックス
		groupId: {type: String, default: ''},					// 項目グループID
		reducedDisplayFlg: { type: Boolean, default: false },	// 縮小表示フラグ true:縮小表示、false:通常表示
	},
	emits: ['update:modelValue', 'blur', 'keydown'],
	data: function() {
		return {
			errorFlg: false,
			mobileFlg: false,
			options: [],
		}
	},
	computed: {
		errFlg: function () {
			return this.$store.getters[STORE_ERROR_IS_ERRFLG];
		}
	},
	watch: {
		cdType: async function() {
			// DBからデータを取得しリストを設定
			this.createList( await this.getData() );
		},
		cdHanyo1: async function() {
			// DBからデータを取得しリストを設定
			this.createList( await this.getData() );
		},
		disabledFlg: function( val ) {
			if( val ) {
				// エラークリア
				this.clearError();
			}
		},
		errFlg: function(newValue) {
			if(newValue === false) {
				this.clearError();
			}
		},
		listItems: function () {
			// 親から受け取ったjson文字列でリストを設定
			this.createList(this.listItems);
		}
	},
	created: async function() {

		this.mobileFlg = this.$store.getters[STORE_VIEW_IS_MOBILE];

		if (!co_isEmpty(this.cdType)) {
			// DBからデータを取得しリストを設定
			this.createList( await this.getData() );

		} else if(!co_isEmpty(this.listItems))  {
			// 親から受け取ったjson文字列でリストを設定
			this.createList( this.listItems );
		}
	},
	beforeUnmount: function() {
		this.options = null;
	},
	methods: {
		// コードマスタからリストの値を取得
		getData: function() {

			// ajaxにてサーバーと通信
			let params = { cdType: this.cdType, cdHanyo1: this.cdHanyo1, cdHanyo2: this.cdHanyo2, cdHanyo3: this.cdHanyo3 };
			return co_post( API_LIST_ITEMS, params );
		},
		// リスト作成
		createList: function( val ) {

			let list = [];

			if ( this.blankFlg ) {
				// 先頭に空白を追加
				list.push( {key: '', val: ''} );
			}

			if ( val != null ) {
				for ( let i = 0; i < val.length; i++ ) {
					// リストを追加
					this.addList( val[i], list);
				}
			}

			this.options = list;

			if (!this.blankFlg && co_isEmpty(this.modelValue) && !co_isEmpty(list)) {
				// リストの先頭の選択値を初期化
				this.$emit( 'update:modelValue', list[0].key );
			}

		},
		addList: function( val , list ) {

			if(this.cdKbn === CD_HANDLE_TYPE.CD_HANYO1) {
				// 汎用コード１をコード値として使用
				list.push( {key: val.cdHanyo1, val: val.nmCd} );
			} else if (this.cdKbn === CD_HANDLE_TYPE.CD_HANYO2) {
				// 汎用コード２をコード値として使用
				list.push( {key: val.cdHanyo2, val: val.nmCd} );
			} else if (this.cdKbn === CD_HANDLE_TYPE.CD_HANYO3) {
				// 汎用コード３をコード値として使用
				list.push( {key: val.cdHanyo3, val: val.nmCd} );
			} else if (this.cdKbn === CD_HANDLE_TYPE.MIX_HANYO21) {
				// 汎用コード２ + 汎用コード１をコード値として使用
				list.push( {key: val.cdHanyo2 + val.cdHanyo1, val: val.nmCd} );
			} else if (this.cdKbn === CD_HANDLE_TYPE.MIX_CD_HANYO1) {
				// コード値 + 汎用コード１をコード値として使用
				list.push( {key: val.idCd + val.cdHanyo1, val: val.nmCd} );
			} else {
				// コード値をコード値として使用
				list.push( {key: val.idCd, val: val.nmCd} );
			}
			
		},
		// 入力イベント
		onChangeValue:function (e) {
			const target = e.target;
			// 親コントロールに入力値を連携
			this.$emit('update:modelValue', target.value);
		},
		// 妥当性チェック
		validate: function () {

			let error = false;
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];

			// 必須チェック
			if ( this.requireFlg === true ) {
				if ( co_isEmpty(this.modelValue) ) {
					this.$store.commit( STORE_ERROR_SET_INPUT_ERR,
									{id: this.groupId, msg: co_getMessage('E0054', this.name)});
					error = true;
				}
			}

			if (!inputError && error) {
				// エラーの先頭項目の場合、フォーカス設定
				this.focus();
			}

			this.errorFlg = error;
			return !this.errorFlg;
		},
		// エラー設定
		setError: function() {
			this.errorFlg = true;
		},
		// エラークリア
		clearError: function() {
			this.errorFlg = false;
		},
		// フォーカス設定
		focus: function() {
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];
			let mobileFlg = this.$store.getters[STORE_VIEW_IS_MOBILE];
			if (inputError || !mobileFlg) {
				this.$refs.selectListBox.focus();
			}
		}
	}
}
</script>

<style scoped>
</style>